import { useState } from 'react';
import { useTheme } from '@/context/ThemeContext';

const samsunDistricts = [
  'Alaçam',
  'Asarcık',
  'Atakum',
  'Ayvacık',
  'Bafra',
  'Canik',
  'Çarşamba',
  'Havza',
  'İlkadım',
  'Kavak',
  'Ladik',
  'Ondokuzmayıs',
  'Salıpazarı',
  'Tekkeköy',
  'Terme',
  'Vezirköprü',
  'Yakakent',
];

const odaSayilari = [
  'Stüdyo',
  '1+0',
  '1+1',
  '2+0',
  '2+1',
  '3+0',
  '3+1',
  '4+0',
  '4+1',
  '5+1',
  '6+1',
  '7+1',
  '8+1',
  '9+1',
];

interface AddFizboModalProps {
  onClose: () => void;
  onAdd: (data: {
    ownerName: string;
    phone: string;
    propertyType: string;
    transactionType: string;
    location: string;
    status: string;
    notes: string;
    price?: number;
    sqm?: number;
    roomCount?: string;
    listingDate?: string;
    reminderOption?: string;
    freeReminder?: { date: string; task: string };
  }) => Promise<void>;
}

export default function AddFizboModal({ onClose, onAdd }: AddFizboModalProps) {
  const { isDark } = useTheme();
  const [form, setForm] = useState({
    ownerName: '',
    phone: '',
    propertyType: 'Daire',
    transactionType: 'Satılık',
    location: '',
    district: '',
    status: 'Düşünecek',
    notes: '',
    price: '',
    sqm: '',
    roomCount: '2+1',
    listingDate: '',
  });
  const [reminderOption, setReminderOption] = useState<'none' | 'free' | 'auto'>('none');
  const [freeReminderDate, setFreeReminderDate] = useState('');
  const [freeReminderTask, setFreeReminderTask] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const locationFull = form.district
      ? `${form.district}, ${form.location}`
      : form.location;

    await onAdd({
      ownerName: form.ownerName,
      phone: form.phone,
      propertyType: form.propertyType,
      transactionType: form.transactionType,
      location: locationFull,
      status: form.status,
      notes: form.notes,
      price: form.price ? Number(form.price) : undefined,
      sqm: form.sqm ? Number(form.sqm) : undefined,
      roomCount: form.roomCount || undefined,
      listingDate: form.listingDate || undefined,
      reminderOption,
      freeReminder:
        reminderOption === 'free' && freeReminderDate && freeReminderTask
          ? { date: freeReminderDate, task: freeReminderTask }
          : undefined,
    });
    setIsSubmitting(false);
  };

  const inputClass = `w-full px-3 py-2 rounded-lg text-sm border outline-none transition-colors ${
    isDark
      ? 'bg-dark-bg border-dark-border text-dark-text placeholder:text-dark-muted focus:border-primary-500'
      : 'bg-light-bg border-light-border text-light-text placeholder:text-light-muted focus:border-primary-500'
  }`;

  const labelClass = `block text-xs font-medium mb-1 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div
        className={`relative w-full max-w-lg rounded-2xl p-5 md:p-6 shadow-glass max-h-[90vh] overflow-y-auto ${
          isDark ? 'bg-dark-card border border-dark-border' : 'bg-white border border-light-border'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h2 className={`text-lg font-semibold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
            Yeni FİZBO Ekle
          </h2>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${
              isDark ? 'hover:bg-dark-border text-dark-muted' : 'hover:bg-light-border text-light-muted'
            }`}
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Ad Soyad</label>
              <input
                type="text"
                value={form.ownerName}
                onChange={(e) => setForm({ ...form, ownerName: e.target.value })}
                placeholder="Ahmet Yılmaz"
                className={inputClass}
                required
              />
            </div>
            <div>
              <label className={labelClass}>Telefon</label>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="0532 xxx xx xx"
                className={inputClass}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Mülk Türü</label>
              <select
                value={form.propertyType}
                onChange={(e) => setForm({ ...form, propertyType: e.target.value })}
                className={inputClass}
              >
                <option value="Daire">Daire</option>
                <option value="Villa">Villa</option>
                <option value="Dükkan">Dükkan</option>
                <option value="Arsa">Arsa</option>
                <option value="İşyeri">İşyeri</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>İşlem Tipi</label>
              <select
                value={form.transactionType}
                onChange={(e) => setForm({ ...form, transactionType: e.target.value })}
                className={inputClass}
              >
                <option value="Satılık">Satılık</option>
                <option value="Kiralık">Kiralık</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Fiyat (₺)</label>
              <input
                type="number"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                placeholder="2850000"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Oda Sayısı</label>
              <select
                value={form.roomCount}
                onChange={(e) => setForm({ ...form, roomCount: e.target.value })}
                className={inputClass}
              >
                {odaSayilari.map((oda) => (
                  <option key={oda} value={oda}>{oda}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className={labelClass}>Alan (m²)</label>
            <input
              type="number"
              value={form.sqm}
              onChange={(e) => setForm({ ...form, sqm: e.target.value })}
              placeholder="120"
              className={inputClass}
            />
          </div>

          {/* İlan Tarihi */}
          <div>
            <label className={labelClass}>İlan Tarihi</label>
            <input
              type="date"
              value={form.listingDate}
              onChange={(e) => setForm({ ...form, listingDate: e.target.value })}
              className={inputClass}
            />
            <p className={`text-xs mt-1 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
              İlanın platformda yayınlandığı tarih
            </p>
          </div>

          {/* Lokasyon — İlçe + Detay */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>İlçe (Samsun)</label>
              <select
                value={form.district}
                onChange={(e) => setForm({ ...form, district: e.target.value })}
                className={inputClass}
              >
                <option value="">İlçe seçin</option>
                {samsunDistricts.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Adres Detayı</label>
              <input
                type="text"
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                placeholder="İnönü Bulvarı, No:12"
                className={inputClass}
                required
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>Durum</label>
            <select
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
              className={inputClass}
            >
              <option value="Kabul">Kabul</option>
              <option value="Düşünecek">Düşünecek</option>
              <option value="Red">Red</option>
              <option value="Başka Emlakçıda">Başka Emlakçıda</option>
            </select>
          </div>

          <div>
            <label className={labelClass}>Notlar</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Görüşme notları..."
              rows={3}
              className={`${inputClass} resize-none`}
            />
          </div>

          {/* Hatırlatma Ekle */}
          <div className={`rounded-xl p-4 border ${isDark ? 'border-dark-border bg-dark-bg' : 'border-light-border bg-light-bg'}`}>
            <p className={`text-sm font-semibold mb-3 ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
              Hatırlatma Ekle
            </p>
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="reminder"
                  checked={reminderOption === 'none'}
                  onChange={() => setReminderOption('none')}
                  className="w-4 h-4 accent-primary-500"
                />
                <span className={`text-sm ${isDark ? 'text-dark-text' : 'text-light-text'}`}>Hatırlatma ekleme</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="reminder"
                  checked={reminderOption === 'free'}
                  onChange={() => setReminderOption('free')}
                  className="w-4 h-4 accent-primary-500"
                />
                <span className={`text-sm ${isDark ? 'text-dark-text' : 'text-light-text'}`}>Serbest tarih ile hatırlatma ekle</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="reminder"
                  checked={reminderOption === 'auto'}
                  onChange={() => setReminderOption('auto')}
                  className="w-4 h-4 accent-primary-500"
                />
                <span className={`text-sm ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
                  İlan tarihinden otomatik: 28 / 58 / 88 gün sonra
                </span>
              </label>
            </div>

            {reminderOption === 'free' && (
              <div className="mt-3 space-y-2">
                <input
                  type="date"
                  value={freeReminderDate}
                  onChange={(e) => setFreeReminderDate(e.target.value)}
                  className={inputClass}
                  required={reminderOption === 'free'}
                />
                <input
                  type="text"
                  value={freeReminderTask}
                  onChange={(e) => setFreeReminderTask(e.target.value)}
                  placeholder="Hatırlatma notu..."
                  className={inputClass}
                  required={reminderOption === 'free'}
                />
              </div>
            )}

            {reminderOption === 'auto' && (
              <div className="mt-3 p-3 rounded-lg bg-orange-50 dark:bg-orange-900/20">
                <p className={`text-xs ${isDark ? 'text-orange-300' : 'text-orange-700'}`}>
                  İlan tarihine göre otomatik 3 hatırlatma oluşturulacak:
                </p>
                <ul className={`text-xs mt-1 space-y-1 ${isDark ? 'text-orange-200' : 'text-orange-600'}`}>
                  <li>• 28 gün sonra — FİZBO takip</li>
                  <li>• 58 gün sonra — Güncelleme kontrolü</li>
                  <li>• 88 gün sonra — Acil müdahale</li>
                </ul>
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className={`flex-1 py-2.5 rounded-lg font-medium text-sm border transition-colors ${
                isDark
                  ? 'border-dark-border text-dark-muted hover:bg-dark-border'
                  : 'border-light-border text-light-muted hover:bg-light-bg'
              }`}
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 py-2.5 rounded-lg font-medium text-sm bg-primary-500 hover:bg-primary-600 text-white transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                'Kaydet'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}